import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { usePage } from '../contexts/CmsContext';
import { getDeptIcon } from '../lib/deptIcons';
import { 
  GraduationCap, 
  BookOpen, 
  Clock, 
  Users, 
  Microscope,
  Stethoscope,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const ACADEMICS_HERO = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1920&q=80";

const departments = [
  { name: 'Anatomy', category: 'Pre-Clinical' },
  { name: 'Physiology', category: 'Pre-Clinical' },
  { name: 'Biochemistry', category: 'Pre-Clinical' },
  { name: 'Pharmacology', category: 'Para-Clinical' },
  { name: 'Pathology', category: 'Para-Clinical' },
  { name: 'Microbiology', category: 'Para-Clinical' },
  { name: 'Community Medicine', category: 'Para-Clinical' },
  { name: 'Forensic Medicine', category: 'Para-Clinical' },
  { name: 'General Medicine', category: 'Clinical' },
  { name: 'Paediatrics', category: 'Clinical' },
  { name: 'Dermatology & Venereology', category: 'Clinical' },
  { name: 'Psychiatry', category: 'Clinical' },
  { name: 'General Surgery', category: 'Clinical' },
  { name: 'Orthopaedics', category: 'Clinical' },
  { name: 'Oto-Rhino-Laryngology (ENT)', category: 'Clinical' },
  { name: 'Ophthalmology', category: 'Clinical' },
  { name: 'Obst. & Gynaecology', category: 'Clinical' },
  { name: 'Anaesthesiology', category: 'Clinical' },
  { name: 'Radio-Diagnosis', category: 'Clinical' },
  { name: 'Dentistry', category: 'Clinical' },
  { name: 'Respiratory Medicine', category: 'Clinical' },
  { name: 'Oncology / Cancer', category: 'Super Specialty' },
  { name: 'Neurology', category: 'Super Specialty' },
  { name: 'Cardiology', category: 'Super Specialty' },
  { name: 'Urology (Medicine & Surgery)', category: 'Super Specialty' },
  { name: 'Paediatric Surgery', category: 'Super Specialty' },
];

const programHighlights = [
  {
    title: 'Duration',
    value: '5.5 Years',
    description: 'Including 1 year internship',
    icon: Clock,
  },
  {
    title: 'Annual Intake',
    value: '200',
    description: 'Students',
    icon: Users,
  },
  {
    title: 'Clinical Training',
    value: 'Year 2',
    description: 'Early patient exposure',
    icon: Stethoscope,
  },
  {
    title: 'Recognition',
    value: 'NMC',
    description: 'Fully recognized program',
    icon: GraduationCap,
  },
];

const teachingApproach = [
  'Competency-Based Medical Education (CBME) aligned with NMC guidelines',
  'Early Clinical Exposure (ECE) from the first year',
  'Integrated teaching combining basic and clinical sciences',
  'Problem-based learning and case discussions',
  'Regular assessments and feedback mechanisms',
  'Research orientation and scientific temper development',
  'Communication skills and ethics training',
  'Community-based learning through health camps',
];

const Academics = () => {
  const { data: academicsCms } = usePage('academics');
  const cmsHighlights = academicsCms?.program_highlights || [];
  const highlights = programHighlights.map((h, i) => ({
    ...h,
    value: cmsHighlights[i]?.value ?? h.value,
    description: cmsHighlights[i]?.description ?? h.description,
    title: cmsHighlights[i]?.title ?? h.title,
  }));
  return (
    <div className="page-transition" data-testid="academics-page">
      {/* Hero Section */}
      <section 
        className="relative py-20 md:py-32 hero-overlay"
        style={{
          backgroundImage: `url(${ACADEMICS_HERO})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="academics-hero"
      >
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              Academic Programs
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
              MBBS Program
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              A comprehensive medical education program designed to produce competent, ethical, and compassionate physicians ready to serve the healthcare needs of society.
            </p>
          </div>
        </div>
      </section>

      {/* Program Highlights */}
      <section className="py-12 bg-white border-b border-gray-100" data-testid="program-highlights">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {highlights.map((item, index) => (
              <div 
                key={index}
                className="text-center p-6"
                data-testid={`highlight-${index}`}
              >
                <div className="w-14 h-14 bg-[#4ECDC4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="text-[#4ECDC4]" size={24} />
                </div>
                <p className="font-heading text-2xl md:text-3xl font-bold text-[#1A202C]">{item.value}</p>
                <p className="text-sm text-gray-600 mt-1">{item.title}</p>
                <p className="text-xs text-gray-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="program-overview">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-medium mb-4">
                MBBS Program
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-6">
                Program Overview
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  The Bachelor of Medicine and Bachelor of Surgery (MBBS) program at SSMCH is a comprehensive five-and-a-half-year course that prepares students for a career in medicine. The program follows the Competency-Based Medical Education (CBME) curriculum prescribed by the National Medical Commission (NMC).
                </p>
                <p>
                  Our curriculum emphasizes early clinical exposure, integrated teaching, and hands-on training to ensure students develop strong clinical skills alongside theoretical knowledge. Students benefit from extensive training at our attached teaching hospital, gaining exposure to diverse medical cases.
                </p>
                <p>
                  The program includes a mandatory one-year rotating internship where students work across various clinical departments, gaining practical experience under the supervision of senior faculty members.
                </p>
              </div>
            </div>
            <div>
              <Card className="border-0 shadow-lg">
                <CardHeader className="bg-[#1A202C] text-white rounded-t-xl">
                  <CardTitle className="font-heading text-xl">Program Structure</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-[#4ECDC4] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1A202C]">Phase I (1 year)</h4>
                        <p className="text-sm text-gray-600">Pre-clinical subjects: Anatomy, Physiology, Biochemistry</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-[#4ECDC4] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1A202C]">Phase II (1.5 years)</h4>
                        <p className="text-sm text-gray-600">Para-clinical subjects: Pathology, Microbiology, Pharmacology, Forensic Medicine, Community Medicine</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-[#4ECDC4] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1A202C]">Phase III (2 years)</h4>
                        <p className="text-sm text-gray-600">Clinical subjects: Medicine, Surgery, OBG, Paediatrics, and other clinical specialties</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-[#D4A84B] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">4</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1A202C]">Internship (1 year)</h4>
                        <p className="text-sm text-gray-600">Compulsory rotating internship across clinical departments</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Departments */}
      <section className="section-padding bg-white" data-testid="departments-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
              Academic Departments
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-4">
              Departments
            </h2>
            <p className="text-gray-600 text-lg">
              Our college comprises well-equipped departments staffed by experienced faculty members.
            </p>
          </div>

          {['Pre-Clinical', 'Para-Clinical', 'Clinical', 'Super Specialty'].map(cat => {
            const catDepts = departments.filter(d => d.category === cat);
            return (
              <div key={cat} className="mb-10">
                <h3 className="font-heading text-xl font-bold text-[#1A202C] mb-4 border-l-4 border-[#4ECDC4] pl-3">{cat}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {catDepts.map((dept, index) => {
                    const DeptIcon = getDeptIcon(dept.name);
                    return (
                    <Card 
                      key={index}
                      className="card-hover group border border-gray-100 shadow-none hover:shadow-md"
                      data-testid={`department-${dept.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
                    >
                      <CardContent className="p-4 text-center">
                        <div className="w-12 h-12 bg-[#4ECDC4]/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-[#4ECDC4]/20 transition-colors">
                          <DeptIcon className="text-[#4ECDC4]" size={20} />
                        </div>
                        <p className="text-sm font-medium text-[#1A202C]">{dept.name}</p>
                      </CardContent>
                    </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Teaching Approach */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="teaching-approach">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-[#9ACD32]/10 text-[#9ACD32] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Our Approach
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-6">
                Teaching Methodology
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                SSMCH follows modern teaching methodologies aligned with the Competency-Based Medical Education (CBME) framework. Our approach emphasizes active learning, clinical correlation, and professional development.
              </p>
              <ul className="space-y-4">
                {teachingApproach.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-[#4ECDC4] flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="font-heading text-xl font-semibold text-[#1A202C] mb-6">
                Learning Resources
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#4ECDC4]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <BookOpen className="text-[#4ECDC4]" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A202C] mb-1">Central Library</h4>
                    <p className="text-sm text-gray-600">Extensive collection of medical textbooks, journals, and digital resources</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#4ECDC4]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Microscope className="text-[#4ECDC4]" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A202C] mb-1">Modern Laboratories</h4>
                    <p className="text-sm text-gray-600">Well-equipped labs for practical training in all pre-clinical and para-clinical subjects</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#4ECDC4]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Stethoscope className="text-[#4ECDC4]" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A202C] mb-1">Clinical Skills Lab</h4>
                    <p className="text-sm text-gray-600">Simulation-based training for procedural skills development</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#4ECDC4]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="text-[#4ECDC4]" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A202C] mb-1">Teaching Hospital</h4>
                    <p className="text-sm text-gray-600">Multi-specialty hospital providing diverse clinical exposure</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A202C]" data-testid="academics-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Start Your Medical Education?
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Explore our admission process and take the first step towards becoming a healthcare professional.
          </p>
          <Link to="/admissions">
            <Button 
              className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-full px-8 h-12 font-medium shadow-lg hover:shadow-xl transition-all"
              data-testid="academics-apply-btn"
            >
              View Admission Details
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Academics;
